// Sends one small Google Analytics event per meaningful tool action (Format, Copy, Convert...), without any content.
// Lets us tell "people never arrive" from "people arrive and leave"; see docs/architecture/analytics.md.

/** The tools that report usage. A closed set so event data stays comparable between tools. */
export type ToolName = "json" | "xml" | "yaml" | "csv" | "sql" | "diff" | "jsonpath" | "base64" | "url-encoder" | "converter";

/** What the visitor did. "edit" means they typed or pasted into the editor, the weakest sign of real use. */
export type ToolAction =
    | "edit"
    | "validate"
    | "format"
    | "minify"
    | "convert"
    | "compare"
    | "snapshot"
    | "mode_change"
    | "copy"
    | "download";

/** `window.gtag` as defined by the inline script in src/components/GoogleAnalytics; absent when the script is blocked or not yet loaded. */
type GtagFunction = (command: "event", eventName: string, parameters: Record<string, string>) => void;

/** Tool/action pairs already reported on this page view; "edit" fires on every keystroke, so it is sent only once. */
const reportedEdits = new Set<ToolName>();

/**
 * Reports `tool_action` with the tool and action names (plus an optional `detail` such as "json-to-xml" for the
 * converters). Never pass user content: the text people paste can be private. Does nothing when analytics is
 * unavailable, so tools work the same with an ad blocker.
 */
export const trackToolAction = (tool: ToolName, action: ToolAction, detail?: string): void => {
    if (typeof window === "undefined") return;
    if (action === "edit") {
        if (reportedEdits.has(tool)) return;
        reportedEdits.add(tool);
    }
    const gtag = (window as unknown as { gtag?: GtagFunction }).gtag;
    if (typeof gtag !== "function") return;
    gtag("event", "tool_action", {
        tool_name: tool,
        tool_action: action,
        ...(detail ? { detail } : {}),
    });
};
